import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  canAccessPath,
  getDefaultAuthorizedPath,
  getPermittedPaths,
  hasPermission,
  IntegrationPermissionEnum,
} from "../../config/access";
import { SoulDataModel } from "../models/Soul/SoulDataModel";
import { soulManager } from "../managers/SoulManager";
import { useAuth } from "./AuthProvider";

type SoulContextValue = {
  soul?: SoulDataModel;
  loading: boolean;
  permittedPaths: string[];
  defaultAuthorizedPath: string | null;
  canAccessPath: (path: string) => boolean;
  hasPermission: (permission: IntegrationPermissionEnum) => boolean;
  refreshSoul: () => Promise<void>;
};

const SoulContext = createContext<SoulContextValue>({
  soul: undefined,
  loading: true,
  permittedPaths: [],
  defaultAuthorizedPath: null,
  canAccessPath: () => false,
  hasPermission: () => false,
  refreshSoul: async () => {},
});

export const SoulProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const [soul, setSoul] = useState<SoulDataModel | undefined>();
  const [loading, setLoading] = useState(true);

  const refreshSoul = useCallback(async () => {
    if (!user) {
      setSoul(undefined);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const nextSoul = await soulManager.getSoul();
      setSoul(nextSoul);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    void refreshSoul();
  }, [authLoading, refreshSoul]);

  const integrations = soul?.integrations || [];

  const value = useMemo<SoulContextValue>(
    () => ({
      soul,
      loading: authLoading || loading,
      permittedPaths: getPermittedPaths(integrations),
      defaultAuthorizedPath: getDefaultAuthorizedPath(integrations),
      canAccessPath: (path: string) => canAccessPath(integrations, path),
      hasPermission: (permission: IntegrationPermissionEnum) =>
        hasPermission(integrations, permission),
      refreshSoul,
    }),
    [authLoading, integrations, loading, refreshSoul, soul]
  );

  return <SoulContext.Provider value={value}>{children}</SoulContext.Provider>;
};

export const useSoulAccess = () => useContext(SoulContext);
